/**
 * Topics API client
 *
 * Provides methods for interacting with the Topic endpoints of Task Manager API.
 */

import { fetchApi, buildQueryString } from './client';

// Topics API base path - same service as tasks
const TASKS_API_BASE = import.meta.env.VITE_TASKS_API_BASE ?? '/tasks';

/**
 * Topic entity
 */
export interface Topic {
  id: string;
  name: string;
  description?: string;
  slug: string;
  path?: string;
  depth: number;
  color?: string;
  icon?: string;
  topicType?: string;
  asanaProjectGid?: string;
  asanaSectionGid?: string;
  individualId: string;
  isActive: boolean;
  isArchived: boolean;
  taskCount: number;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Response for listing topics
 */
export interface TopicsListResponse {
  count: number;
  topics: Topic[];
}

/**
 * Response for a single topic
 */
export interface TopicResponse {
  topic: Topic;
}

/**
 * Response for topic tree
 */
export interface TopicTreeNode extends Topic {
  children: TopicTreeNode[];
}

export interface TopicTreeResponse {
  tree: TopicTreeNode[];
}

/**
 * Response for supporting tasks
 */
export interface SupportingTasksResponse {
  topicId: string;
  taskCount: number;
  tasks: Array<{
    taskId: string;
    edgeId: string;
  }>;
}

/**
 * Response for task's related topics
 */
export interface TaskRelatedTopicsResponse {
  taskId: string;
  topics: Array<{
    topicId: string;
    edgeId: string;
  }>;
}

/**
 * Response for setting task's related topics
 */
export interface SetTaskRelatedTopicsResponse {
  taskId: string;
  topicCount: number;
  edges: Array<{
    id: string;
    edgeType: string;
    fromId: string;
    toId: string;
    source: string;
  }>;
}

// =============================================================================
// TOPICS API
// =============================================================================

/**
 * List all topics
 */
export async function listTopics(options?: {
  type?: string;
  includeArchived?: boolean;
  limit?: number;
}): Promise<TopicsListResponse> {
  const queryParams: Record<string, string | number | boolean | undefined> = {};
  if (options?.type) queryParams.type = options.type;
  if (options?.includeArchived) queryParams.include_archived = options.includeArchived;
  if (options?.limit) queryParams.limit = options.limit;

  const query = buildQueryString(queryParams);
  return fetchApi<TopicsListResponse>(`${TASKS_API_BASE}/topics${query}`);
}

/**
 * Get a single topic by ID
 */
export async function getTopic(topicId: string): Promise<TopicResponse> {
  return fetchApi<TopicResponse>(`${TASKS_API_BASE}/topics/${topicId}`);
}

/**
 * Get topic tree structure
 */
export async function getTopicTree(): Promise<TopicTreeResponse> {
  return fetchApi<TopicTreeResponse>(`${TASKS_API_BASE}/topics/tree`);
}

/**
 * Create a new topic
 */
export async function createTopic(input: {
  name: string;
  description?: string;
  topicType?: string;
  color?: string;
  icon?: string;
  parentId?: string;
}): Promise<TopicResponse> {
  return fetchApi<TopicResponse>(`${TASKS_API_BASE}/topics`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update a topic
 */
export async function updateTopic(
  topicId: string,
  input: Partial<{
    name: string;
    description: string;
    color: string;
    icon: string;
    topicType: string;
  }>
): Promise<TopicResponse> {
  return fetchApi<TopicResponse>(`${TASKS_API_BASE}/topics/${topicId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a topic
 */
export async function deleteTopic(topicId: string): Promise<{ success: boolean }> {
  return fetchApi<{ success: boolean }>(`${TASKS_API_BASE}/topics/${topicId}`, {
    method: 'DELETE',
  });
}

/**
 * Archive a topic
 */
export async function archiveTopic(topicId: string): Promise<TopicResponse> {
  return fetchApi<TopicResponse>(`${TASKS_API_BASE}/topics/${topicId}/archive`, {
    method: 'POST',
  });
}

/**
 * Unarchive a topic
 */
export async function unarchiveTopic(topicId: string): Promise<TopicResponse> {
  return fetchApi<TopicResponse>(`${TASKS_API_BASE}/topics/${topicId}/unarchive`, {
    method: 'POST',
  });
}

// =============================================================================
// TASK-TOPIC RELATIONSHIPS (RELATED_TO edges)
// =============================================================================

/**
 * Get topics that a task is related to
 */
export async function getTaskRelatedTopics(taskId: string): Promise<TaskRelatedTopicsResponse> {
  return fetchApi<TaskRelatedTopicsResponse>(`${TASKS_API_BASE}/${taskId}/related-topics`);
}

/**
 * Set the topics that a task is related to (replaces existing relations)
 */
export async function setTaskRelatedTopics(
  taskId: string,
  topicIds: string[],
  source: string = 'manual'
): Promise<SetTaskRelatedTopicsResponse> {
  return fetchApi<SetTaskRelatedTopicsResponse>(`${TASKS_API_BASE}/${taskId}/related-topics`, {
    method: 'PUT',
    body: JSON.stringify({ topicIds, source }),
  });
}

/**
 * Remove a single task-topic relation
 */
export async function removeTaskTopicRelation(
  taskId: string,
  topicId: string
): Promise<{ success: boolean; taskId: string; topicId: string }> {
  return fetchApi<{ success: boolean; taskId: string; topicId: string }>(
    `${TASKS_API_BASE}/${taskId}/related-topics/${topicId}`,
    { method: 'DELETE' }
  );
}

/**
 * Get supporting tasks for a topic (inverse of task's related topics)
 */
export async function getSupportingTasks(topicId: string): Promise<SupportingTasksResponse> {
  return fetchApi<SupportingTasksResponse>(`${TASKS_API_BASE}/topics/${topicId}/supporting-tasks`);
}

/**
 * Remove a supporting task from a topic
 */
export async function removeSupportingTask(
  topicId: string,
  taskId: string
): Promise<{ success: boolean; taskId: string; topicId: string }> {
  return fetchApi<{ success: boolean; taskId: string; topicId: string }>(
    `${TASKS_API_BASE}/topics/${topicId}/supporting-tasks/${taskId}`,
    { method: 'DELETE' }
  );
}

